import React, { PropsWithChildren, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { LeftNavButton, RightNavButton } from './SliderComponents';
import { useWindowResize } from './useWindowResize';
import { useCalcTrackOffset } from './hooks/useCalcTrackOffset';
import { useAnimateSlideJump } from './hooks/useAnimateSlideJump';
import { useUpdateItems } from './hooks/useUpdateItems';

interface Props {
    showButtons?: boolean;
    showDots?: boolean;
    centerMode?: boolean;
    animationSlideTime?: number;
    itemWidth?: number;
    itemHeight?: number;
    buttonPosition?: string;
    minimumItemsToShow?: number;
    showShadows?: boolean;
    initialSlide?: boolean;
}

export interface State {
    parentWidth: number;
    items: ReactNode[];
    initialSlide: boolean;
    visibleItems: number;
    neededItems: number;
    mounted: boolean;
    lastIndexClick: number | undefined;
    bannerGreaterThanScreen: boolean;
    showShadows: boolean;
    showButtons: boolean;
    centerMode: boolean;
    showDots: boolean;
}

export const BannerSlider = ({
    itemWidth = 2000,
    itemHeight = 200,
    showDots = true,
    initialSlide = true,
    animationSlideTime = 750,
    centerMode = true,
    showButtons = true,
    buttonPosition = 'middle',
    children
}: PropsWithChildren<Props>) => {
    const bannerSliderDots = useRef<HTMLUListElement>(null);
    const bannerSlider = useRef<HTMLDivElement>(null);
    const bannerSliderTrack = useRef<HTMLDivElement>(null);
    const offsetValue = useRef(0);
    const prevOffset = useRef(0);
    const directionValue = useRef(0);
    const lock = useRef(false);
    const hovered = useRef(false);

    const [state, setState] = useState<State>({
        parentWidth: 2000,
        items: [],
        initialSlide: false,
        visibleItems: 0,
        neededItems: 0,
        mounted: false,
        lastIndexClick: undefined,
        bannerGreaterThanScreen: false,
        showShadows: true,
        showButtons,
        centerMode,
        showDots
    });

    const { calcTrackOffset } = useCalcTrackOffset({
        itemWidth,
        offsetValue: offsetValue.current,
        centerMode: state.centerMode,
        bannerGreaterThanScreen: state.bannerGreaterThanScreen,
        parentWidth: state.parentWidth,
        numberOfItems: state.items.length
    });

    const instantJumpToSlide = useCallback(
        pos => {
            if (bannerSliderTrack.current) {
                bannerSliderTrack.current.style.left = `${calcTrackOffset(pos)}px`;
            }
        },
        [calcTrackOffset]
    );

    const childrenCount = Array.isArray(children) ? children.length : 0;

    const { animateSlideJump, animateSlide, dotPosition } = useAnimateSlideJump({
        calcTrackOffset,
        instantJumpToSlide,
        childrenCount,
        offsetValue,
        lock,
        bannerSliderTrack,
        animationSlideTime
    });

    const prepareSlideJump = useCallback(() => {
        const endPos = offsetValue.current;
        const direction = directionValue.current;
        let middlePos;
        let jumpPos;
        let animationTime2;
        let animationTime1;
        if (direction > 0) {
            middlePos = childrenCount;
            jumpPos = 0;
            const slideCount = Math.abs(directionValue.current);

            animationTime1 = animationSlideTime * ((childrenCount - prevOffset.current) / slideCount);
            animationTime2 = animationSlideTime * (endPos / slideCount);
        } else {
            middlePos = 0;
            jumpPos = childrenCount;
            const slideCount = Math.abs(directionValue.current);

            animationTime1 = animationSlideTime * (prevOffset.current / slideCount);
            animationTime2 = animationSlideTime * ((childrenCount - endPos) / slideCount);
        }

        animateSlideJump(middlePos, endPos, jumpPos, animationTime1, animationTime2);
    }, [animateSlideJump, animationSlideTime, childrenCount]);

    const slideOffset = useCallback(
        (offset: number) => {
            prevOffset.current = offsetValue.current;
            let nextOffset = offsetValue.current + offset;
            const dir = offset;
            directionValue.current = dir;
            let slideJump = false;

            if (nextOffset >= childrenCount && dir > 0) {
                nextOffset = nextOffset % childrenCount;
                slideJump = true;
            } else if (nextOffset < 0 && dir < 0) {
                nextOffset = childrenCount + nextOffset;
                slideJump = true;
            }
            offsetValue.current = nextOffset;

            lock.current = true;
            if (!slideJump) {
                animateSlide();
            } else {
                prepareSlideJump();
            }
        },
        [animateSlide, prepareSlideJump, childrenCount]
    );

    const slideNext = useCallback(() => {
        if (!state.initialSlide || hovered.current || lock.current || document.hidden) {
            return;
        }
        slideOffset(1);
    }, [slideOffset, state.initialSlide]);

    const { updateItems } = useUpdateItems({
        children,
        itemWidth,
        initialSlide,
        showButtons,
        showDots,
        centerMode
    });

    const handleResize = useCallback(() => {
        const offsetWidth = bannerSlider?.current?.offsetWidth ?? 2000;
        const resizeState = updateItems(offsetWidth);

        setState(state => ({ ...state, ...resizeState, parentWidth: offsetWidth }));
    }, [updateItems]);

    useWindowResize(handleResize);

    const onDotClick = useCallback(
        index => {
            if (lock.current) {
                setState(state => ({ ...state, lastIndexClick: index }));
                return;
            }
            const position = offsetValue.current;
            let diff = index - position;
            if (diff === 0) {
                return;
            }

            if (Math.abs(diff) > childrenCount / 2) {
                if (index < childrenCount / 2) {
                    diff += childrenCount;
                } else {
                    diff -= childrenCount;
                }
                slideOffset(diff);
            } else {
                slideOffset(diff);
            }
        },
        [lock, childrenCount, slideOffset]
    );

    useEffect(() => {
        if (state.lastIndexClick && state.lastIndexClick >= 0 && !lock.current) {
            onDotClick(state.lastIndexClick);
            setState(state => ({ ...state, lastIndexClick: undefined }));
        }
    }, [state.lastIndexClick, lock, onDotClick]);

    useEffect(() => {
        if (children) {
            setState(state => ({ ...state, ...updateItems(state.parentWidth) }));
        }
    }, [children, updateItems, state.parentWidth]);

    useEffect(() => {
        const initialSlideTimer = window.setInterval(slideNext, 5000);
        const mountedTimeout = window.setTimeout(() => setState(state => ({ ...state, mounted: true })), 4000);

        return () => {
            if (initialSlideTimer) {
                clearInterval(initialSlideTimer);
            }
            if (mountedTimeout) {
                window.clearTimeout(mountedTimeout);
            }
        };
    }, [slideNext]);

    function onMouseOver(over: boolean) {
        hovered.current = over;
    }

    function onSlideButtonClicked(direction: number) {
        if (!lock.current) {
            slideOffset(direction);
        }
    }

    const trackOffset = calcTrackOffset(offsetValue.current);

    return (
        <div
            ref={bannerSlider}
            data-displayname="BannerSlider"
            data-testid="BannerSlider"
            className={classNames('banner-slider', { mounted: state.mounted })}
            style={{ height: `${itemHeight}px`, width: '100%' }}
            onMouseEnter={() => {
                onMouseOver(true);
            }}
            onMouseLeave={() => {
                onMouseOver(false);
            }}
        >
            {state.showButtons ? (
                <LeftNavButton
                    onClick={() => onSlideButtonClicked(-1)}
                    showShadows={state.showShadows}
                    itemHeight={itemHeight}
                    buttonPosition={buttonPosition}
                />
            ) : null}
            <div
                className={classNames('banner-slider-track')}
                ref={bannerSliderTrack}
                style={{ width: `${itemWidth * state.items.length}px`, left: `${trackOffset}px` }}
            >
                {state.items.map((item, index) => {
                    return (
                        <div key={index} style={{ width: `${itemWidth}px`, height: `${itemHeight}px` }}>
                            {item}
                        </div>
                    );
                })}
            </div>
            {state.showButtons ? (
                <RightNavButton
                    onClick={() => onSlideButtonClicked(1)}
                    itemHeight={itemHeight}
                    buttonPosition={buttonPosition}
                />
            ) : null}
            {state.showDots ? (
                <ul className="banner-slider-dots" ref={bannerSliderDots}>
                    {Array.isArray(children)
                        ? children.map((child, index) => {
                              return (
                                  <li key={index}>
                                      <button
                                          onClick={() => onDotClick(index)}
                                          className={index === dotPosition ? 'active' : ''}
                                      >
                                          {index}
                                      </button>
                                  </li>
                              );
                          })
                        : null}
                </ul>
            ) : null}
        </div>
    );
};
